/**
 * SystemStatusIcons - Single source of truth for hardware system status icon rendering.
 *
 * Used in:
 * - Map hover cards (UnitPreviewCard) via variant="detailed"
 * - Settings data table (Units.tsx) via variant="compact"
 * - Any future location needing system status indicators
 *
 * Color logic:
 * - Solar (Sun):     text-amber-400 when generation > 0
 * - Charger (Zap):   text-blue-400  when powerOut > 0
 * - HVAC (Wind):     text-cyan-400  when mode !== 'off'
 * - Starlink (Wifi): text-green-400 when connectionStatus === 'online'
 * - All default to:  text-muted-foreground/30
 */

import { Sun, Plug, Zap, Wind, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Unit } from '@/types';
import {
  type ChargeSource,
  type SystemType,
  chargeSlotAppearance,
  getSystemIconColor,
  resolveSystemMetrics,
} from './SystemStatusIcons.helpers';

interface SystemStatusIconsProps {
  unit: Unit;
  variant: 'compact' | 'detailed';
  /** Icon size class (default: 'h-4 w-4') */
  iconSize?: string;
  /** Gap between icons (default: 'gap-2' for compact, 'gap-3' for detailed) */
  className?: string;
  /** Click handler per system (used in map hover cards for tab navigation) */
  onSystemClick?: (system: SystemType) => void;
  /** Whether this is a mobile context (affects tooltip delay) */
  isMobile?: boolean;
  /**
   * Override the charger "active" state instead of deriving it from
   * charger.powerOut. Used by the Alerts page's Sol-Ark EV-charging placeholder
   * until real Tesla charger telemetry lands. Omit for the default behavior.
   */
  chargerActiveOverride?: boolean;
  /**
   * Server-classified charging source (fleet-health `charge_source`). When passed
   * — including `null` — the first slot shows the charging SOURCE: amber sun for
   * solar, neutral plug for shore power, muted sun when not charging. Omit it to
   * keep the generation-based sun (map cards, settings table).
   */
  chargeSource?: ChargeSource | null;
}

export function SystemStatusIcons({
  unit,
  variant,
  iconSize = 'h-4 w-4',
  className,
  onSystemClick,
  isMobile: _isMobile = false,
  chargerActiveOverride,
  chargeSource,
}: SystemStatusIconsProps) {
  const metrics = resolveSystemMetrics(unit);

  const solarActive = metrics.solarGeneration > 0;
  // First slot: the charging SOURCE when fleet-health supplies it, else the
  // original generation-based sun.
  const sourceAware = chargeSource !== undefined;
  const slot = sourceAware ? chargeSlotAppearance(chargeSource) : null;
  const SlotIcon = slot?.icon === 'plug' ? Plug : Sun;
  const slotColor = slot ? slot.colorClass : getSystemIconColor('solar', solarActive);
  const slotTitle = slot
    ? slot.title
    : solarActive ? 'Solar: Generating' : unit.systems?.solar?.enabled ? 'Solar: Idle' : 'Solar: Not linked';
  const chargerActive = chargerActiveOverride ?? (metrics.chargerPower > 0);
  const hvacActive = metrics.hvacMode !== 'off';
  const starlinkOnline = metrics.starlinkStatus === 'online';

  const defaultGap = variant === 'compact' ? 'gap-2' : 'gap-3';

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center', defaultGap, className)}>
        <span title={slotTitle}>
          <SlotIcon className={cn(iconSize, slotColor)} />
        </span>
        <span title={chargerActive ? 'Charger: Active' : unit.systems?.charger?.enabled ? 'Charger: Idle' : 'Charger: Not linked'}>
          <Zap className={cn(iconSize, getSystemIconColor('charger', chargerActive))} />
        </span>
        <span title={hvacActive ? `HVAC: ${metrics.hvacMode}` : unit.systems?.hvac?.enabled ? 'HVAC: Off' : 'HVAC: Not linked'}>
          <Wind className={cn(iconSize, getSystemIconColor('hvac', hvacActive))} />
        </span>
        <span title={starlinkOnline ? 'Starlink: Online' : unit.systems?.starlink?.enabled ? `Starlink: ${metrics.starlinkStatus}` : 'Starlink: Not linked'}>
          <Wifi className={cn(iconSize, getSystemIconColor('starlink', starlinkOnline))} />
        </span>
      </div>
    );
  }

  // detailed variant - with click handlers for tab navigation
  const handleClick = (system: SystemType) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onSystemClick?.(system);
  };

  return (
    <div className={cn('flex items-center', defaultGap, className)}>
      <div onClick={handleClick('solar')} title={slotTitle} className="cursor-pointer hover:opacity-80 transition-opacity">
        <SlotIcon className={cn(iconSize, slotColor)} />
      </div>
      <div onClick={handleClick('charger')} className="cursor-pointer hover:opacity-80 transition-opacity">
        <Zap className={cn(iconSize, getSystemIconColor('charger', chargerActive))} />
      </div>
      <div onClick={handleClick('hvac')} className="cursor-pointer hover:opacity-80 transition-opacity">
        <Wind className={cn(iconSize, getSystemIconColor('hvac', hvacActive))} />
      </div>
      <button
        onClick={handleClick('starlink')}
        className="p-1 transition-opacity active:opacity-70 cursor-pointer hover:opacity-80"
        title="Starlink"
      >
        <Wifi className={cn(iconSize, getSystemIconColor('starlink', starlinkOnline))} />
      </button>
    </div>
  );
}
